import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ContractTypeDto, getContractTypes } from '../api/contract-types';
import { useAuth } from './auth';

interface ContractTypesContextValue {
  contractTypes: ContractTypeDto[];
  loading: boolean;
  refresh: () => void;
  /** For SelectBox/Lookup: array of { Oid, Name } items */
  contractTypeItems: { value: string; text: string }[];
  /** For Scheduler calendar resources: same shape as old contractTypesResources */
  contractTypeResources: { key: string; items: { id: string; text: string; color: string; checkboxColor: string }[] }[];
  /** Flat list for Scheduler Resource dataSource */
  resourcesList: { id: string; text: string; color: string; checkboxColor: string }[];
  /** Get color by Oid */
  getColor: (oid: string) => string;
  /** Get name by Oid */
  getName: (oid: string) => string;
}

const ContractTypesContext = createContext<ContractTypesContextValue>({
  contractTypes: [],
  loading: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  refresh: () => {},
  contractTypeItems: [],
  contractTypeResources: [],
  resourcesList: [],
  getColor: () => '#2196f3',
  getName: () => '',
});

export const useContractTypes = () => useContext(ContractTypesContext);

export const ContractTypesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [contractTypes, setContractTypes] = useState<ContractTypeDto[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async() => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const items = await getContractTypes(user.token);
      setContractTypes(items.filter((i) => i.IsActive));
    } catch {
      // silently fail - may not have contract types yet
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const contractTypeItems = useMemo(
    () => contractTypes.map((ct) => ({ value: ct.Oid, text: ct.Name })),
    [contractTypes]
  );

  const contractTypeResources = useMemo(
    () => [
      {
        key: 'Contract Types',
        items: contractTypes.map((ct) => ({
          id: ct.Oid,
          text: ct.Name,
          color: ct.Color,
          checkboxColor: ct.Color,
        })),
      },
    ],
    [contractTypes]
  );

  const resourcesList = useMemo(
    () =>
      contractTypes.map((ct) => ({
        id: ct.Oid,
        text: ct.Name,
        color: ct.Color,
        checkboxColor: ct.Color,
      })),
    [contractTypes]
  );

  const getColor = useCallback(
    (oid: string) => {
      const ct = contractTypes.find((c) => c.Oid === oid);
      return ct?.Color || '#2196f3';
    },
    [contractTypes]
  );

  const getName = useCallback(
    (oid: string) => {
      const ct = contractTypes.find((c) => c.Oid === oid);
      return ct?.Name || '';
    },
    [contractTypes]
  );

  const value = useMemo(
    () => ({
      contractTypes,
      loading,
      refresh,
      contractTypeItems,
      contractTypeResources,
      resourcesList,
      getColor,
      getName,
    }),
    [contractTypes, loading, refresh, contractTypeItems, contractTypeResources, resourcesList, getColor, getName]
  );

  return <ContractTypesContext.Provider value={value}>{children}</ContractTypesContext.Provider>;
};
